import React, { useState, useEffect } from 'react';
import { Section, StudentProgress, Answer } from '../types';
import { evaluateAnswer } from '../utils/evaluation';
import { quizSections } from '../data/questions';

interface EnhancedSectionViewProps {
  section: Section;
  progress: StudentProgress;
  onProgressUpdate: (progress: StudentProgress) => void;
  onSectionComplete: () => void;
  onNextSection?: () => void;
  onSubmitQuiz?: () => void;
  onGoToFirstUnanswered?: () => void;
}

const EnhancedSectionView: React.FC<EnhancedSectionViewProps> = ({
  section,
  progress,
  onProgressUpdate,
  onSectionComplete,
  onNextSection,
  onSubmitQuiz,
  onGoToFirstUnanswered
}) => {
  const [answers, setAnswers] = useState<{ [questionId: string]: any }>({});
  const [debounceTimers, setDebounceTimers] = useState<{ [questionId: string]: NodeJS.Timeout }>({});

  const currentSectionProgress = progress.sections[progress.currentSection];


  // Initialize answers from existing progress (without evaluation)
  useEffect(() => {
    const existingAnswers: { [questionId: string]: any } = {};

    if (currentSectionProgress?.answers) {
      currentSectionProgress.answers.forEach(answer => {
        if (answer.type === 'multiple-choice') {
          existingAnswers[answer.questionId] = answer.selectedOption;
        } else if (answer.type === 'free-response') {
          existingAnswers[answer.questionId] = answer.freeResponse || '';
        }
      });
    }

    setAnswers(existingAnswers);
  }, [currentSectionProgress]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [debounceTimers]);

  const handleAnswerChange = async (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Store answer in progress immediately (without evaluation)
    const question = section.questions.find(q => q.id === questionId);
    if (!question) return;

    const answer: Answer = {
      questionId: question.id,
      type: question.type,
      selectedOption: question.type === 'multiple-choice' ? value : undefined,
      freeResponse: question.type === 'free-response' ? value : undefined,
      isCorrect: false, // Temporary - will be properly evaluated on quiz submit
      score: 0,
      attemptCount: 0,
    };

    // Debug mode: Evaluation in development
    if (process.env.NODE_ENV === 'development') {
      // Clear any existing timer for this question
      if (debounceTimers[questionId]) {
        clearTimeout(debounceTimers[questionId]);
      }

      // For multiple choice: evaluate immediately
      if (question.type === 'multiple-choice') {
        try {
          const evaluation = await evaluateAnswer(question, value, undefined);
          displayDebugResults(questionId, question, value, evaluation, 'multiple-choice');
        } catch (error) {
          console.error('🚨 DEBUG: Evaluation error for question', questionId, ':', error);
        }
      }
      // For free response: wait 5 seconds after user stops typing
      else if (question.type === 'free-response' && value && value.trim() !== '') {
        const newTimer = setTimeout(async () => {
          try {
            console.log(`⏱️ DEBUG: Evaluating free response for ${questionId} after 5 second delay...`);
            const evaluation = await evaluateAnswer(question, undefined, value);
            displayDebugResults(questionId, question, value, evaluation, 'free-response');
          } catch (error) {
            console.error('🚨 DEBUG: Evaluation error for question', questionId, ':', error);
          }
        }, 5000);

        setDebounceTimers(prev => ({
          ...prev,
          [questionId]: newTimer
        }));
      }
    }

    // Update progress with unevaluated answer
    const updatedSectionProgress = {
      ...currentSectionProgress,
      answers: (currentSectionProgress?.answers || []).filter(a => a.questionId !== questionId).concat(answer),
    };

    const updatedProgress = {
      ...progress,
      sections: progress.sections.map((section, index) =>
        index === progress.currentSection ? updatedSectionProgress : section
      ),
    };

    onProgressUpdate(updatedProgress);
  };

  const displayDebugResults = (questionId: string, question: any, value: any, evaluation: any, questionType: string) => {
    console.group(`🔍 DEBUG: Question ${questionId} Evaluation`);
    console.log('Question:', question.question);
    console.log('Answer:', questionType === 'multiple-choice' ?
      `Option ${value + 1}: ${question.options?.[value]}` :
      value);
    console.log('Result:', evaluation.isCorrect ? '✅ CORRECT' : '❌ INCORRECT');
    console.log('Score:', evaluation.score);
    console.log('Feedback:', evaluation.feedback);

    if (evaluation.detailedFeedback) {
      if (evaluation.detailedFeedback.correctPoints.length > 0) {
        console.log('✅ Correct Points:', evaluation.detailedFeedback.correctPoints);
      }
      if (evaluation.detailedFeedback.incorrectPoints.length > 0) {
        console.log('❌ Incorrect Points:', evaluation.detailedFeedback.incorrectPoints);
      }
      if (evaluation.detailedFeedback.missedPoints.length > 0) {
        console.log('⚠️ Missed Points:', `(${evaluation.detailedFeedback.missedPoints.length})`, evaluation.detailedFeedback.missedPoints);
      }
    }

    if (evaluation.geminiScore !== undefined) {
      console.log('🤖 Gemini Score:', evaluation.geminiScore);
      console.log('🤖 Gemini Confidence:', evaluation.geminiConfidence);
    }

    console.groupEnd();

    // Visual debug indicator
    const questionElement = document.getElementById(`question-${questionId}`);
    if (questionElement) {
      // Remove any existing debug indicators
      const existingIndicator = questionElement.querySelector('.debug-indicator');
      if (existingIndicator) {
        existingIndicator.remove();
      }

      // Add new debug indicator
      const debugIndicator = document.createElement('div');
      debugIndicator.className = 'debug-indicator absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold';
      debugIndicator.style.cssText = evaluation.isCorrect ?
        'background-color: #10b981; color: white;' :
        'background-color: #ef4444; color: white;';
      debugIndicator.textContent = evaluation.isCorrect ?
        `DEBUG: ✓ (${evaluation.score})` :
        `DEBUG: ✗ (${evaluation.score})`;

      // Make question container relative for positioning
      questionElement.style.position = 'relative';
      questionElement.appendChild(debugIndicator);

      // Remove indicator after 5 seconds
      setTimeout(() => {
        debugIndicator.remove();
      }, 5000);
    }
  };

  const isQuestionAnswered = (questionId: string) => {
    return answers[questionId] !== undefined && answers[questionId] !== null &&
           (typeof answers[questionId] === 'string' ? answers[questionId].trim() !== '' : true);
  };

  const visibleQuestions = section.questions.filter(question => {
    // Hide questions that were previously answered correctly in partial retakes
    const previousAnswer = currentSectionProgress?.answers.find(a => a.questionId === question.id);
    return !previousAnswer || !previousAnswer.isCorrect;
  });

  const getOriginalQuestionIndex = (question: any) => {
    // Find the original index of this question in the full section
    return section.questions.findIndex(q => q.id === question.id) + 1;
  };

  // Check if there are unanswered questions in the current section
  const hasUnansweredInCurrentSection = () => {
    const currentSectionProgress = progress.sections[progress.currentSection];
    for (const question of section.questions) {
      const existingAnswer = currentSectionProgress?.answers.find(a => a.questionId === question.id);
      if (!existingAnswer ||
          (question.type === 'multiple-choice' && existingAnswer.selectedOption === undefined) ||
          (question.type === 'free-response' && (!existingAnswer.freeResponse || existingAnswer.freeResponse.trim() === ''))) {
        return true;
      }
    }
    return false;
  };

  // Check if there are unanswered questions in ANY section (for last section)
  const hasUnansweredInAnySection = () => {
    for (let sectionIndex = 0; sectionIndex < quizSections.length; sectionIndex++) {
      const sectionData = quizSections[sectionIndex];
      const sectionProgress = progress.sections[sectionIndex];

      for (const question of sectionData.questions) {
        const existingAnswer = sectionProgress?.answers.find(a => a.questionId === question.id);
        if (!existingAnswer ||
            (question.type === 'multiple-choice' && existingAnswer.selectedOption === undefined) ||
            (question.type === 'free-response' && (!existingAnswer.freeResponse || existingAnswer.freeResponse.trim() === ''))) {
          return true;
        }
      }
    }
    return false;
  };

  // Handle go to first unanswered with section-specific logic
  const handleGoToFirstUnanswered = () => {
    const isLastSection = progress.currentSection === quizSections.length - 1;

    if (isLastSection) {
      // Last section: search all sections and navigate if needed
      if (onGoToFirstUnanswered) {
        onGoToFirstUnanswered();
      }
    } else {
      // Other sections: only search current section and scroll
      const currentSectionProgress = progress.sections[progress.currentSection];
      for (const question of section.questions) {
        const existingAnswer = currentSectionProgress?.answers.find(a => a.questionId === question.id);
        if (!existingAnswer ||
            (question.type === 'multiple-choice' && existingAnswer.selectedOption === undefined) ||
            (question.type === 'free-response' && (!existingAnswer.freeResponse || existingAnswer.freeResponse.trim() === ''))) {

          const element = document.getElementById(`question-${question.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      }
    }
  };

  // Determine if "Go to First Unanswered" button should be shown
  const shouldShowGoToFirstUnanswered = () => {
    const isLastSection = progress.currentSection === quizSections.length - 1;
    return isLastSection ? hasUnansweredInAnySection() : hasUnansweredInCurrentSection();
  };

  return (
    <div className="space-y-8">
      {/* Questions */}
      <div className="space-y-8">
        {visibleQuestions.map((question, index) => {
          const isAnswered = isQuestionAnswered(question.id);

          return (
            <div key={question.id} id={`question-${question.id}`} className="card p-6">
              {/* Question Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-gray-500 mr-2">
                      Question {getOriginalQuestionIndex(question)}
                    </span>
                    {isAnswered && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Answered
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {question.question}
                  </h3>
                </div>
              </div>

              {/* Question Image */}
              {question.image && (
                <div className="mb-4">
                  <img
                    src={question.image}
                    alt={question.imageAlt || 'Question diagram'}
                    className="max-w-full h-auto rounded-lg border border-gray-200"
                  />
                </div>
              )}

              {/* Answer Input */}
              <div className="mb-4">
                {question.type === 'multiple-choice' && question.options && (
                  <div className="space-y-3">
                    {question.options.map((option, optionIndex) => (
                      <label
                        key={optionIndex}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                          answers[question.id] === optionIndex
                            ? 'border-safety-500 bg-safety-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={optionIndex}
                          checked={answers[question.id] === optionIndex}
                          onChange={() => handleAnswerChange(question.id, optionIndex)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                          answers[question.id] === optionIndex ? 'border-safety-500' : 'border-gray-300'
                        }`}>
                          {answers[question.id] === optionIndex && (
                            <div className="w-2 h-2 rounded-full bg-safety-500"></div>
                          )}
                        </div>
                        <span className="text-gray-900">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'free-response' && (
                  <div>
                    <textarea
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      placeholder="Enter your detailed answer here..."
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-safety-500"
                    />
                    {question.minPoints && (
                      <p className="text-sm text-gray-600 mt-2">
                        Please include at least {question.minPoints} key safety points in your answer.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          {/* Left side - Go to first unanswered question */}
          <div className="flex-shrink-0">
            {shouldShowGoToFirstUnanswered() && (
              <button
                onClick={handleGoToFirstUnanswered}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-safety-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                {progress.currentSection === quizSections.length - 1
                  ? 'Go to First Unanswered (Any Section)'
                  : 'Go to First Unanswered'
                }
              </button>
            )}
          </div>

          {/* Right side - Next section or Submit */}
          <div className="flex gap-3">
            {/* Show Next Section button if not on last section */}
            {progress.currentSection < quizSections.length - 1 && onNextSection && (
              <button
                onClick={onNextSection}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-safety-600 hover:bg-safety-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-safety-500"
              >
                Next Section
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Show Submit Quiz button if on last section */}
            {progress.currentSection === quizSections.length - 1 && onSubmitQuiz && (
              <button
                onClick={onSubmitQuiz}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Submit Quiz
              </button>
            )}
          </div>
        </div>

        {/* Section Progress Summary */}
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Section {progress.currentSection + 1} of {quizSections.length}: {section.name}
          </p>
          {progress.currentSection === quizSections.length - 1 && (
            <p className="text-xs text-gray-500 mt-1">
              This is the final section. Review your answers and submit when ready.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedSectionView;